/** Passkeys from the browser's side (ADR-0105).
 *
 * **Proven only against fakes here and a software authenticator on the server.** No real device
 * has run this path; the screens that use it say so rather than promising it works.
 *
 * Two things are deliberate:
 *
 * - **Signing in returns a result, not an exception, for the outcomes a person causes.** A refused
 *   assertion, a dismissed prompt and a browser that cannot do it are three results the form words
 *   differently; only a request that never completed throws, like `useLogin`.
 * - **Adding a passkey asks for the password in the same breath and keeps neither it nor the
 *   response**: `gcTime: 0`, so the mutation's variables do not sit in the query cache.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { authKeys } from "./auth";
import type { LoginResult } from "./auth";
import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";
import { createPasskey, getPasskey, PasskeyBrowserError } from "./webauthn";

export type Passkey = components["schemas"]["PasskeyOut"];

export const passkeyKeys = {
  list: () => ["profile", "passkeys"] as const,
};

function isPasskey(value: unknown): value is Passkey {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const fields = value as Record<string, unknown>;
  return (
    typeof fields.id === "string" &&
    typeof fields.label === "string" &&
    typeof fields.created_at === "string" &&
    (fields.last_used_at === null || typeof fields.last_used_at === "string") &&
    typeof fields.synced === "boolean"
  );
}

/** The result of trying to sign in with a passkey. */
export type PasskeySignInResult =
  | LoginResult
  | { status: "cancelled" }
  | { status: "unsupported" }
  | { status: "unavailable" };

export function usePasskeySignIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<PasskeySignInResult> => {
      const started = await api.POST("/api/v1/auth/passkeys/authentication/options", {});
      if (started.response.status === 503) return { status: "unavailable" };
      if (!started.response.ok || !started.data) return { status: "refused" };

      let credential: Record<string, unknown>;
      try {
        credential = await getPasskey(started.data.options);
      } catch (error) {
        if (error instanceof PasskeyBrowserError) {
          if (error.kind === "cancelled") return { status: "cancelled" };
          if (error.kind === "unsupported") return { status: "unsupported" };
        }
        return { status: "refused" };
      }

      const { data, response } = await api.POST("/api/v1/auth/passkeys/authentication/verify", {
        body: { ceremony_id: started.data.ceremony_id, credential },
      });
      // Every refusal is the same 401, by design: the form shows one sentence.
      if (response.status === 503) return { status: "unavailable" };
      if (!response.ok || !data) return { status: "refused" };
      if (data.mfa_required) {
        return { status: "mfa_required", enrolmentRequired: data.mfa_enrolment_required };
      }
      return { status: "signed_in" };
    },
    onSuccess: async (result) => {
      if (result.status === "signed_in") {
        await queryClient.invalidateQueries({ queryKey: authKeys.me() });
      }
    },
  });
}

export function usePasskeys() {
  return useQuery({
    queryKey: passkeyKeys.list(),
    retry: false,
    queryFn: async (): Promise<Passkey[]> => {
      const { data, error, response } = await api.GET("/api/v1/profile/passkeys", {});
      if (!response.ok || !Array.isArray(data) || !data.every(isPasskey)) {
        throw refusalFrom(response, error, "Your passkeys could not be read.");
      }
      return data;
    },
  });
}

/** Add a passkey: ask the server for options (proving the password), let the browser make the
 * credential, send it back. Throws `Refused` for a server refusal and `PasskeyBrowserError` for
 * the browser's, so a form can word each beside what caused it. */
export function useAddPasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: async (input: { currentPassword: string; label: string }): Promise<Passkey> => {
      const started = await api.POST("/api/v1/profile/passkeys/registration/options", {
        body: { current_password: input.currentPassword },
      });
      if (!started.response.ok || !started.data) {
        throw refusalFrom(started.response, started.error, "A passkey could not be started.");
      }
      const credential = await createPasskey(started.data.options);
      const { data, error, response } = await api.POST(
        "/api/v1/profile/passkeys/registration/verify",
        {
          body: {
            ceremony_id: started.data.ceremony_id,
            label: input.label,
            credential,
          },
        },
      );
      if (!response.ok || !isPasskey(data)) {
        throw refusalFrom(response, error, "The passkey could not be saved.");
      }
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: passkeyKeys.list() });
    },
  });
}

export function useRemovePasskey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error, response } = await api.DELETE("/api/v1/profile/passkeys/{passkey_id}", {
        params: { path: { passkey_id: id } },
      });
      if (!response.ok) throw refusalFrom(response, error, "The passkey could not be removed.");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: passkeyKeys.list() });
    },
  });
}
